/**
 * @author Maxime Mustarda <maxime@inarix.com>
 * @file LicencesTable.tsx
 * @desc Created on Mon Apr 28 2025 20:44:52
 * @copyright All rights reserved @ Inarix
 */

import { FC, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Alert, Box, Modal, Snackbar, ThemeProvider } from "@mui/material";
import { DataGridPro, useGridApiRef } from "@mui/x-data-grid-pro";

import themeInarix from "../StylesMuiInarix";
import { selectLocale } from "../../redux/slices/layout";
import {
  focusedLicense,
  licenseAssignation,
  clearLicenseNotification,
  selectLicenseLoading,
  selectLicenseNotification,
  selectModalLicense,
  setLicenseId,
  LicensesFilters,
} from "../../redux/slices/licenses/licenseList";
import { LicensePacksModal } from "./LicensePacksModal";

import { useLicenseColumns } from "./LicenceColumns";
import LicenseAssignationModal from "./LicenseAssignationModal";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { TFunction } from "i18next";
import {
  getDataGridLocale,
  managingFiltersText,
} from "../data/dataHelpers/DataProgress";

type LicencesTableParams = {
  apiRef: ReturnType<typeof useGridApiRef>;
  initData: (dispatch: ThunkDispatch<any, any, any>) => void;
  t: TFunction<"global", undefined>;
};

const SetActions = (dispatch: ThunkDispatch<any, any, any>) => {
  return {
    setLicenseId: (id: string) => dispatch(setLicenseId(id)),
    focusedLicense: (id: string) => dispatch(focusedLicense(id)),
  };
};

export const LicencesTable: FC<LicencesTableParams> = ({
  apiRef,
  initData,
  t,
}) => {
  const dispatch = useDispatch();
  const locale = useSelector(selectLocale);
  const loading = useSelector(selectLicenseLoading);
  const modalLicense = useSelector(selectModalLicense);
  const { licenseId } = useSelector(licenseAssignation);
  const notification = useSelector(selectLicenseNotification);
  const { licenseColumns, licensesRows } = useLicenseColumns(t, dispatch);
  const { setLicenseId, focusedLicense } = SetActions(dispatch);
  const [loadingAssignLicense, setLoadingAssignLicense] = useState(false);

  const { filterModel } = useSelector(LicensesFilters);

  return (
    <Box>
      <ThemeProvider theme={themeInarix(locale)}>
        <div className="container-licenses-table">
          <Box sx={{ height: "58vh", typography: "body1" }}>
            <DataGridPro
              apiRef={apiRef}
              rows={licensesRows}
              columns={licenseColumns}
              rowHeight={70}
              pagination
              disableRowSelectionOnClick={true}
              disableColumnSelector={true}
              initialState={{
                pagination: { paginationModel: { pageSize: 25 } },
              }}
              loading={loading}
              pageSizeOptions={[25, 50, 100]}
              ignoreDiacritics={true}
              localeText={{
                ...getDataGridLocale(locale),
                noRowsLabel: managingFiltersText(filterModel, t, licensesRows),
              }}
            />
          </Box>
        </div>
        <Modal open={!!modalLicense} onClose={() => focusedLicense("")}>
          <LicensePacksModal />
        </Modal>

        <Modal
          open={!!licenseId}
          onClose={loadingAssignLicense ? undefined : () => setLicenseId("")}
        >
          <LicenseAssignationModal
            initData={initData}
            t={t}
            setLoadingAssignLicense={setLoadingAssignLicense}
            loadingAssignLicense={loadingAssignLicense}
          />
        </Modal>

        <Snackbar
          open={notification.open}
          autoHideDuration={4000}
          onClose={() => dispatch(clearLicenseNotification())}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={() => dispatch(clearLicenseNotification())}
            severity={notification.severity}
          >
            {t(`licenses.${notification.message}`)}
          </Alert>
        </Snackbar>
      </ThemeProvider>
    </Box>
  );
};
